import { z } from 'zod'

/**
 * O contrato comum dos microsservicos de gateway de pagamento.
 * Fonte da verdade: docs/adr/0016-checkout-como-microsservico.md
 *
 * Todo gateway de pagamento tem um processo proprio (`apps/infinitepay` e o primeiro), e todos
 * expoem as MESMAS quatro rotas. Quem consome e o `apps/checkout`, que fala este vocabulario e
 * nenhum outro — e e isso que mantem `handle`, `order_nsu` e `slug` confinados ao processo que
 * conversa com a InfinitePay.
 *
 * Este arquivo mora em `packages/contracts` porque ele nao importa nada do monorepo
 * (docs/03-arquitetura.md secao 2) e ja e o lugar dos tipos compartilhados. Um pacote novo so para
 * isto seria uma quarta borda a manter, sem nada em troca.
 *
 * ATENCAO: nada aqui cobra dinheiro. Enquanto o conteudo usar propriedade intelectual de terceiro,
 * a cobranca fica desligada por configuracao (docs/99-aviso-legal.md).
 */

// ---------------------------------------------------------------------------
// Vocabulario
// ---------------------------------------------------------------------------

/**
 * Os estados de uma cobranca, iguais para todos os gateways.
 *
 * Uniao de strings literais, e nao `enum` do TypeScript (docs/14-convencoes.md secao 3). Cada
 * servico de gateway TRADUZ o vocabulario proprio para esta lista, e e nessa traducao que o
 * dialeto de cada um para de existir para o resto do sistema.
 *
 * `CANCELED` e `EXPIRED` sao coisas diferentes de proposito: a primeira e alguem desistindo, a
 * segunda e o prazo acabando. Junta-las apagaria a unica informacao que distingue um problema de
 * produto de um problema de fluxo.
 */
export const PAYMENT_STATUSES = ['CREATED', 'PENDING', 'PAID', 'FAILED', 'CANCELED', 'EXPIRED', 'REFUNDED'] as const
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number]

/**
 * Como o pagador pagou. `UNKNOWN` existe porque a cobranca nasce antes de a escolha acontecer, e
 * um campo opcional aqui obrigaria todo consumidor a tratar `undefined` para ler algo que sempre
 * tem um valor conhecido.
 */
export const CAPTURE_METHODS = ['UNKNOWN', 'PIX', 'CREDIT_CARD', 'DEBIT_CARD', 'BOLETO'] as const
export type CaptureMethod = (typeof CAPTURE_METHODS)[number]

/**
 * Os codigos de erro que um gateway pode devolver. Erro tipado com codigo, nunca string solta
 * (docs/14 secao 5).
 *
 * `GATEWAY_DISABLED` nao e um erro de quem chamou: e o servico dizendo que a cobranca esta
 * desligada por configuracao (docs/99). Ele existe separado de `GATEWAY_ERROR` para que a pessoa
 * que ve a resposta va para a variavel de ambiente, e nao procurar defeito na rede.
 */
export const GATEWAY_ERROR_CODES = [
  'GATEWAY_DISABLED',
  'GATEWAY_ERROR',
  'GATEWAY_UNAVAILABLE',
  'INVALID_REQUEST',
  'PAYMENT_NOT_FOUND',
  'NOT_CANCELABLE',
  'NOT_IMPLEMENTED',
] as const
export type GatewayErrorCode = (typeof GATEWAY_ERROR_CODES)[number]

// ---------------------------------------------------------------------------
// Blocos reaproveitados
// ---------------------------------------------------------------------------

/**
 * Dinheiro e sempre inteiro, em centavos.
 *
 * E o que a InfinitePay exige ("o preco sempre vai em centavos") e o que `Purchase.amountCents` ja
 * usa do outro lado. Ponto flutuante em dinheiro nao entra no contrato: `0.1 + 0.2` continua sendo
 * o argumento, e um centavo perdido por arredondamento e uma divergencia de conciliacao
 * (docs/10-seguranca-antifraude.md secao 5).
 */
export const amountCentsSchema = z.int().positive()

/** Identificador opaco. Quem o gera e o gateway; quem o le nao interpreta o formato. */
const opaqueIdSchema = z.string().min(1).max(120)

export const gatewayItemSchema = z.object({
  /** Descricao que o pagador ve na tela do gateway. */
  description: z.string().min(1).max(200),
  quantity: z.int().positive(),
  /** Preco unitario. O gateway multiplica; nao se envia o total do item. */
  unitAmountCents: amountCentsSchema,
})
export type GatewayItem = z.infer<typeof gatewayItemSchema>

/**
 * Dados do pagador. Todos OPCIONAIS, e isso e uma decisao e nao uma folga: o gateway os usa para
 * pre-preencher a tela dele, e exigi-los faria o checkout guardar dado pessoal que ele nao precisa
 * guardar (docs/10 secao 6).
 */
export const gatewayCustomerSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  email: z.email().optional(),
  phone: z.string().min(1).max(30).optional(),
})
export type GatewayCustomer = z.infer<typeof gatewayCustomerSchema>

// ---------------------------------------------------------------------------
// POST /payments — cria a cobranca
// ---------------------------------------------------------------------------

export const createPaymentRequestSchema = z.object({
  /**
   * O pedido no `apps/checkout`. Referencia por VALOR: o pedido vive em outro banco, e nao ha
   * chave estrangeira possivel entre dois bancos (docs/04-modelo-de-dados.md).
   */
  orderId: z.uuid(),
  /** O total. O gateway confere contra a soma dos itens e recusa se divergirem. */
  amountCents: amountCentsSchema,
  /** ISO 4217. O gateway recusa moeda que nao atende. */
  currencyCode: z.string().length(3).default('BRL'),
  items: z.array(gatewayItemSchema).min(1),
  customer: gatewayCustomerSchema.optional(),
  /** Para onde o navegador do pagador volta. NAO e canal de confirmacao (docs/06 secao 10.1). */
  returnUrl: z.url(),
  /**
   * Pedir a cobranca duas vezes devolve a MESMA cobranca. E a regra que o repositorio aplica a
   * tudo que move valor (docs/04 secao 1), e aqui ela protege o clique duplo em "finalizar".
   */
  idempotencyKey: z.string().min(8).max(120),
})
export type CreatePaymentRequest = z.infer<typeof createPaymentRequestSchema>

export const paymentViewSchema = z.object({
  /** O id da cobranca NO gateway-servico. O checkout guarda como `Order.gatewayRef`. */
  paymentId: opaqueIdSchema,
  /** Qual servico respondeu (`infinitepay`, `mock`, ...). */
  gateway: z.string().min(1).max(40),
  orderId: z.uuid(),
  status: z.enum(PAYMENT_STATUSES),
  amountCents: amountCentsSchema,
  /**
   * O que o pagador de fato pagou, quando difere do cobrado — juros de parcelamento entram aqui.
   * Nulo enquanto ninguem pagou. Uma coluna so faria parecer que o pedido mudou de preco.
   */
  paidAmountCents: z.int().positive().nullable(),
  captureMethod: z.enum(CAPTURE_METHODS),
  installments: z.int().positive().nullable(),
  /** Para onde mandar o pagador. Ausente quando nao ha tela a visitar. */
  paymentUrl: z.url().nullable(),
  /** O comprovante, quando o gateway emite um. */
  receiptUrl: z.url().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  paidAt: z.iso.datetime().nullable(),
})
export type PaymentView = z.infer<typeof paymentViewSchema>

// ---------------------------------------------------------------------------
// O aviso de volta — POST /internal/payments/:paymentId/status no checkout
// ---------------------------------------------------------------------------

/**
 * O que o servico de gateway envia ao checkout quando um estado muda.
 *
 * Descrever os DOIS sentidos aqui e deliberado: um so descreveria metade da integracao, e a metade
 * que falta e justamente a que move dinheiro.
 *
 * O que este aviso NAO faz: creditar. Ele diz que uma cobranca mudou de estado. Quem confirmou o
 * pagamento na origem foi o servico do gateway, e quem credita gema e o `apps/api`, num terceiro
 * lugar (ADR 0004).
 */
export const paymentStatusNoticeSchema = z.object({
  payment: paymentViewSchema,
  /**
   * Quando o gateway-servico observou a mudanca. E o relogio DELE: quem recebe nao mistura este
   * carimbo com o proprio `now()` para decidir regra (docs/14 secao 5.1).
   */
  observedAt: z.iso.datetime(),
})
export type PaymentStatusNotice = z.infer<typeof paymentStatusNoticeSchema>

// ---------------------------------------------------------------------------
// Erro
// ---------------------------------------------------------------------------

export const gatewayErrorSchema = z.object({
  code: z.enum(GATEWAY_ERROR_CODES),
  message: z.string().min(1),
})
export type GatewayError = z.infer<typeof gatewayErrorSchema>

// ---------------------------------------------------------------------------
// GET /health
// ---------------------------------------------------------------------------

export const gatewayHealthSchema = z.object({
  service: z.string().min(1),
  status: z.literal('ok'),
  /**
   * Se a cobranca esta ligada. O `/health` responde `ok` de qualquer forma: o servico esta de pe,
   * e desligado nao e doente. Quem precisa da distincao le este campo.
   */
  enabled: z.boolean(),
})
export type GatewayHealth = z.infer<typeof gatewayHealthSchema>
